import type Person            from '@/domains/Persons/Person';
import type RpcDataPagination from '@/core/services/Rpc/RpcDataPagination';
import type RpcDataSort       from '@/core/services/Rpc/RpcDataSort';
import Core                   from '@/core/Core';
import OrdersDAO              from '@/domains/Orders/OrdersDAO';
import Order                  from '@/domains/Orders/Order';
import OrderPosition          from '@/domains/Orders/OrderPosition';
import AssembliesService      from '@/domains/Assemblies/AssembliesService';
import Assembly               from '@/domains/Assemblies/Assembly';
import Shipment               from '@/domains/Shipments/Shipment';
import ShipmentsService       from '@/domains/Shipments/ShipmentsService';
import TendersService         from '@/domains/Tenders/TendersService';
import PartnersService        from '@/domains/Partners/PartnersService';
import DemandsV2Service       from '@/domains/DemandsV2/DemandsV2Service';
import EntityPointer          from '@/domains/EntityPointer/EntityPointer';
import ContextMenuItem        from '@/lib/components/VContextMenu/ContextMenuItem';
import Dictionary             from '@/core/types/Dictionary';
import PreorderPosition       from '@/domains/Preorders/PreorderPosition';
import OrderCategory          from '@/domains/Orders/OrderCategory';
import OrderPricePoint        from '@/domains/Orders/OrderPricePoint';
import OrderPositionShipmentTranche from '@/domains/Orders/components/OrderPositionShipmentsPopup/OrderPositionShipmentTranche';
/**
 * Сервис домена заказов.
 *
 * Объединяет работу с заказами и их позициями: на беке всё под одним
 * namespace orders.* (один OrdersController), поэтому отдельного
 * OrderPositionsService больше нет — позиционные методы живут здесь
 * рядом с заказными (нейминг с суффиксами Order/Position по контроллеру).
 *
 * Инкапсулирует CRUD, гидрацию, нотификации, показ диалогов и контекстные
 * меню. Обновление реестра/карточки после действия диалоги на себя не берут:
 * хост обновляется сам (результат пункта меню или await → refresh),
 * поэтому методы показа диалогов не прокидывают колбэков.
 */
class OrdersService {

  /**
   * Состояния позиции заказа. Используются:
   * - как опции табов в OrdersPage (id, name);
   * - как маппинг state-пропа OrderPositionsRegistry в бэк-флаги
   *   isInWork / isFinished / isReceived / isDeleted.
   */
  static States = [
    { id: 'in-work',    name: 'В работе',  isInWork:      true },
    { id: 'finished',   name: 'Завершён',  isFinished:    true },
    { id: 'received',   name: 'Получен',   isReceived:    true },
    { id: 'deleted',    name: 'Удалён',    isDeleted:     true },
    { id: 'complaints', name: 'Претензии', hasComplaints: true },
  ] as const;

  /**
   * Кеш категорий заказов. Наполняется из Dictionaries общим эндпоинтом
   * (utilities.getCombinedFrontendData) — потребителей источник не заботит, они читают кеш сервиса.
   * reactive — чтобы шаблоны перерисовывались, когда словарь долетит после логина.
   */
  static orderCategories = reactive(new Dictionary<string, OrderCategory>());


  /**
   * Маппинг id состояния в бэк-флаг: 'in-work' → { isInWork: true } и т.п.
   * Каждый дескриптор в States содержит ровно один флаг кроме id и name —
   * его и выковыриваем. Если состояние не найдено — пустой объект.
   */
  static getStateFlag(stateId: string): Record<string, boolean> {
    const stateDescriptor = OrdersService.States.find(descriptor => descriptor.id === stateId);
    const stateFlag: Record<string, boolean> = {};

    if (stateDescriptor !== undefined) {
      for (const [key, value] of Object.entries(stateDescriptor)) {
        if (key !== 'id' && key !== 'name') {
          stateFlag[key] = value as boolean;
        }
      }
    }

    return stateFlag;
  }

  // ============================================================
  // Загрузка — заказы
  // ============================================================

  static async getOrder(id: number): Promise<Order | null> {
    const response = await OrdersDAO.getOrder(id);

    return Core.utils.isObject(response.data) ? Order.fromServer(response.data) : null;
  }

  /**
   * Загрузить список заказов с пагинацией.
   * Используется в реестре через DataGridLoader.
   */
  static async getOrdersListPaginated(
    params?: Parameters<typeof OrdersDAO.getOrdersList>[0] & {
      pagination?: RpcDataPagination;
      sort?: RpcDataSort;
    }
  ): Promise<{ data: Order[]; pagination: RpcDataPagination }> {
    const { pagination, sort, ...filters } = params ?? {};
    const response = await OrdersDAO.getOrdersList(filters, pagination, sort);

    return {
      data:       Core.utils.hydrateArray(response.data, Order.fromServer),
      pagination: response.meta.pagination,
    };
  }

  /**
   * Загрузить список заказов.
   * Для использования вне реестра (диалоги, автокомплиты).
   */
  static async getOrdersList(
    params?: Parameters<typeof OrdersDAO.getOrdersList>[0]
  ): Promise<Order[]> {
    return (await this.getOrdersListPaginated(params)).data;
  }

  /**
   * Получить счётчики заказов по статусам.
   * Используется для отображения чисел в табах реестра.
   */
  static async getCounts(filters?: {
    responsible?:    { id: number };
    seller?:         { id: number };
    deliveryType?:   { id: number };
    orderCategory?:  { id: number };
    isOfferBased?:   boolean;
    isSamples?:      boolean;
    isForTestsOnly?: boolean;
    deliveryStatus?: string | null;
    paymentStatus?:  string | null;
  }): Promise<Record<string, number> | undefined> {
    const response = await OrdersDAO.getCounts(filters);
    return response.data;
  }

  // ============================================================
  // Загрузка — позиции
  // ============================================================

  // UPD есть ли смысл прибить гвоздями состояние из ядра isAdminMode?
  static async getPosition(id: number, isPreorder?: boolean, isAdminMode?: boolean): Promise<OrderPosition | PreorderPosition | null> {
    const response = await OrdersDAO.getPosition(id, isPreorder, isAdminMode);

    if (Core.utils.isObject(response.data) === false) {
      return null;
    }

    /** Позиция предзаказа — своя сущность: у неё нет оплат и отгрузок,
     *  зато есть период выборки и остатки. */
    if (isPreorder === true) {
      return PreorderPosition.fromServer(response.data);
    }
    else {
      return OrderPosition.fromServer(response.data);
    }
  }

  /**
   * Загрузить список позиций заказов с пагинацией.
   * Основной источник для OrdersPage / OrderPositionsRegistry через DataGridLoader.
   */
  static async getPositionsListPaginated(
    params?: Parameters<typeof OrdersDAO.getPositionsList>[0] & {
      pagination?: RpcDataPagination;
      sort?: RpcDataSort;
    }
  ): Promise<{ data: OrderPosition[]; pagination: RpcDataPagination }> {
    const { pagination, sort, ...filters } = params ?? {};
    const response = await OrdersDAO.getPositionsList(filters, pagination, sort);

    return {
      // fromServer у позиции 2-м аргументом принимает order, поэтому
      // оборачиваем явно, а не отдаём метод в hydrateArray напрямую.
      data: Core.utils.isNotEmptyArray(response.data)
        ? response.data.map((item: any) => OrderPosition.fromServer(item))
        : [],
      pagination: response.meta.pagination,
    };
  }

  /**
   * Загрузить список позиций.
   * Для использования вне реестра.
   */
  static async getPositionsList(
    params?: Parameters<typeof OrdersDAO.getPositionsList>[0]
  ): Promise<OrderPosition[]> {
    return (await this.getPositionsListPaginated(params)).data;
  }

  /**
   * Детали отгрузок/сборок/приёмок по позиции — для попапа в реестрах.
   * Бэк отдаёт агрегат (транши OrderPositionShipmentTranche), а не доменный
   * Shipment: в попапе нужны только тип и id документа, чтобы открыть
   * карточку. Гидрация всё равно обязательна — количества и даты приходят
   * строками, а шаблон зовёт у них format().
   */
  static async getPositionShipmentDetails(position: OrderPosition): Promise<OrderPositionShipmentTranche[]> {
    const response = await OrdersDAO.getShipmentDetails(position.id);

    if (Core.utils.isNotEmptyArray(response.data)) {
      return Core.utils.hydrateArray(response.data, OrderPositionShipmentTranche.fromServer);
    } else {
      return [];
    }
  }

  // ============================================================
  // CRUD — заказы
  // ============================================================

  static async add(order: Order): Promise<boolean> {
    const response = await OrdersDAO.add(order);

    if (response.ok) {
      Core.showNotification.success(`${order.isPreorder ? 'Предзаказ' : 'Заказ'} создан`);
    }

    return response.ok;
  }

  static async updateOrder(order: Order, reasonText?: string): Promise<boolean> {
    const response = await OrdersDAO.update(order, reasonText);

    if (response.ok) {
      Core.showNotification.success('Заказ обновлён');
    }

    return response.ok;
  }

  static async deleteOrder(order: Order, reasonText?: string): Promise<boolean>;
  static async deleteOrder(orderId: number, reasonText?: string): Promise<boolean>;

  static async deleteOrder(order: Order | number, reasonText?: string): Promise<boolean> {
    const id = typeof order === 'number' ? order : order.id;
    const response = await OrdersDAO.deleteOrder(id, reasonText);

    if (response.ok) {
      Core.showNotification.success('Заказ удалён');
    }

    return response.ok;
  }

  /**
   * Удалить заказ с подтверждением (по образцу AssembliesService.deleteWithConfirm).
   * Возвращает true если удаление произошло — вызывающий сам решает что делать.
   */
  static async deleteOrderWithConfirm(order: Order): Promise<boolean> {
    const confirmed = await Core.confirm('Подтверждаете удаление заказа?');

    if (confirmed) {
      const isDeleted = await this.deleteOrder(order);

      if (isDeleted === true) {
        order.isDeleted = true;
      }

      return isDeleted;
    } else {
      return false;
    }
  }

  static async confirm(id: number): Promise<boolean> {
    if (!await Core.dialogs.confirm('Утвердить заказ?', `Заказ №${id}`)) {
      return false;
    }

    const response = await OrdersDAO.confirm(id);

    if (response.ok) {
      Core.showNotification.success('Заказ утверждён');
    }

    return response.ok;
  }

  static async unconfirm(positionId: number): Promise<boolean> {
    if (
      await Core.confirm(
        'Подтверждаете возвращение позиции в черновики? ' +
        'При возвращении в черновики будут возвращены все позиции из заказа',
      )
    ) {
      const response = await OrdersDAO.unconfirm(positionId);

      if (response.ok) {
        Core.showNotification.success('Заказ возвращён в черновики');
      }

      return response.ok;
    }

    return false;
  }

  // ============================================================
  // CRUD — позиции
  // ============================================================

  static async updatePosition(position: OrderPosition, reasonText?: string): Promise<boolean> {
    const response = await OrdersDAO.updatePosition(position, reasonText);

    if (response.ok) {
      Core.showNotification.success('Позиция обновлена');
    }

    return response.ok;
  }

  static async deletePosition(position: OrderPosition | PreorderPosition, reasonText?: string): Promise<boolean> {
    const response = await OrdersDAO.deletePosition(position, reasonText);

    if (response.ok) {
      Core.showNotification.success('Позиция удалена');
    }

    return response.ok;
  }

  /**
   * Удалить позицию с подтверждением (по образцу deleteWithConfirm).
   */
  static async deletePositionWithConfirm(position: OrderPosition | PreorderPosition): Promise<boolean> {
    const { isApproved, reasonText } = await Core.dialogs.confirmWithReason(
      'Подтверждаете удаление позиции?',
      `Позиция №${position.id}`,
    );

    if (isApproved === true) {
      const isDeleted = await this.deletePosition(position, reasonText);

      if (isDeleted === true) {
        position.isDeleted = true;
      }

      return isDeleted;
    } else {
      return false;
    }
  }

  static async closePosition(position: OrderPosition | PreorderPosition, reasonText?: string): Promise<boolean> {
    const response = await OrdersDAO.closePosition(position, reasonText);

    if (response.ok) {
      Core.showNotification.success('Заказ закрыт');
    }

    return response.ok;
  }

  static showOrderPositionCard(position: OrderPosition | PreorderPosition): Promise<boolean>  {
    return Core.dialogs.OrderPositionCard.showAsPromise({
      id:         position?.id,
      isPreorder: position instanceof PreorderPosition,
    });
  }

  /** Карточка позиции заказа по id — когда на руках облегчённая строка (например, график аналитики), а не модель позиции */
  static showOrderPositionCardById(id: number): Promise<boolean> {
    return Core.dialogs.OrderPositionCard.showAsPromise({ id, isPreorder: false });
  }

  static showOrderCard(order: Order | null,
                       newOrderType: string | null = null,
                       orderToCopy: Order | null  = null,
                       isPreorder: boolean | null = null,
                       filterParams: any = null): Promise<boolean> {
    return Core.dialogs.OrderCard.showAsPromise({ id: order?.id,
      newOrderType,
      orderIdToCopy: orderToCopy?.id,
      isPreorder,
      filterParams
    });
  }

  /**
   * Унаследованные файлы заказа: позиции, их потребности, заявки и варианты.
   * Потребность, заявка и вариант, общие для нескольких позиций, берутся один раз.
   * Файлы потребности ведёт покупатель — поставщику список пустой.
   */
  static getInheritedFilesPointers(order: Order): EntityPointer[] {
    const result: EntityPointer[] = [];
    const keys = new Set<string>();

    if (Core.currentPartner.isBuyer === true && Array.isArray(order.positions) === true) {
      const positionTypeName = order.requisites.isPreorder === true ? 'preorderPosition' : 'orderPosition';

      order.positions.forEach(position => {
        EntityPointer.collect([
          EntityPointer.make(positionTypeName, position.id, 'Позиция'),
          EntityPointer.make('demandV2', position.demandV2?.id, 'Потребность'),
          EntityPointer.make('demandRequest', position.demandRequest?.id, 'Заявка'),
          EntityPointer.make('demandOption', position.demandOption?.id, 'Вариант'),
        ]).forEach(pointer => {
          const key = pointer.typeName + ':' + pointer.id;

          if (keys.has(key) === false) {
            keys.add(key);
            result.push(pointer);
          } else {
            // уже добавлен по другой позиции
          }
        });
      });
    } else {
      // поставщик или позиции не загружены
    }

    return result;
  }

  static openFilesDialog(order: Order): Promise<boolean> {
    return Core.dialogs.FilesDialog.showAsPromise({
      title: `Заказ № ${Core.utils.string.formatWithDelimiter(order.id)} от ${Core.utils.date.format(order.createdAt)} `,
      fileEntities: [
        new EntityPointer('order', order.id, 'Заказ № ' + order.id),
      ],
      fileEntitiesInherited: this.getInheritedFilesPointers(order),
    });
  }

  static openNotifyPersonsDialog(entity: Order | OrderPosition | PreorderPosition): Promise<boolean> {
    return Core.dialogs.NotifySellerDialog.showAsPromise({ entity });
  }

  static showFeedDialog(orderId: number): void {
    Core.dialogs.FeedDialog.show({
      entityTypeName: 'order',
      entityId:       orderId,
      showAddComment: false,
    });
  }

  /** Поповер с названиями и артикулами позиции (клик по артикулу в реестрах) */
  static showCodeInfoPopup(position: OrderPosition, target: HTMLElement): void {
    Core.dialogs.OrderPositionCodeInfoPopup.show({ position, target });
  }

  /** Поповер с платежами по позиции заказа (клик по диаграмме оплат в реестрах) */
  static showPositionPaymentsPopup(position: OrderPosition, target: HTMLElement): void {
    Core.dialogs.OrderPositionPaymentsPopup.hideIfOpen();
    Core.dialogs.OrderPositionPaymentsPopup.show({ position, target });
  }

  /** Поповер с отгрузками/сборками по позиции заказа (клик по диаграмме отгрузок в реестрах) */
  static showPositionShipmentsPopup(position: OrderPosition, target: HTMLElement): void {
    Core.dialogs.OrderPositionShipmentsPopup.hideIfOpen();
    Core.dialogs.OrderPositionShipmentsPopup.show({ position, target });
  }

  /** Поповер с условиями оплаты по позиции заказа (даты, pre/postpayment terms, комментарий) */
  static showPositionPaymentTermsPopup(position: OrderPosition, target: HTMLElement): void {
    Core.dialogs.OrderPositionPaymentTermsPopup.hideIfOpen();
    Core.dialogs.OrderPositionPaymentTermsPopup.show({ position, target });
  }

  /** Поповер с описанием, условиями оплаты и характеристиками позиции (клик по названию товара) */
  static showPositionInfoPopup(position: OrderPosition, target: HTMLElement): void {
    Core.dialogs.OrderPositionInfoPopup.hideIfOpen();
    Core.dialogs.OrderPositionInfoPopup.show({ position, target });
  }

  /**
   * Отправить уведомление по заказу или позиции предзаказа.
   * Вместо объектов можно передать id.
   *
   * downloadOnly === true — письмо не отправляется: бэк только формирует вложение
   * и возвращает его, файл сразу уходит в скачивание.
   *
   * sendToSellerAndDownload === true — письмо отправляется, и вместе с ним бэк
   * возвращает то же вложение, чтобы отдать его на скачивание. Читается только
   * при downloadOnly === false: в режиме скачивания файл возвращается и так.
   */
  static async sendNotification(order: Order | number, preorderPosition: undefined, receivers: Person[], templateData?: any, downloadOnly?: boolean, sendToSellerAndDownload? : boolean): Promise<boolean>;
  static async sendNotification(order: undefined, preorderPosition: PreorderPosition | number, receivers: Person[], templateData?: any, downloadOnly?: boolean, sendToSellerAndDownload? : boolean): Promise<boolean>;
  static async sendNotification(
    order: Order | number | undefined,
    preorderPosition: PreorderPosition | number | undefined,
    receivers: Person[],
    templateData?: any,
    downloadOnly: boolean = false,
    sendToSellerAndDownload : boolean = false
  ): Promise<boolean> {
    const normalizedOrder = typeof order === 'number'
      ? { id: order } as Order
      : order;

    const normalizedPosition = typeof preorderPosition === 'number'
      ? { id: preorderPosition } as PreorderPosition
      : preorderPosition;

    const response = await OrdersDAO.sendNotification(
      normalizedOrder,
      normalizedPosition,
      receivers,
      templateData,
      downloadOnly,
      sendToSellerAndDownload
    );

    if (response.ok === true) {
      // файл приходит в ответе в обоих режимах, где его запросили:
      // и когда письмо не отправляется, и когда отправляется вместе со скачиванием
      if (downloadOnly === true || sendToSellerAndDownload === true) {
        // бэк вернул сам файл, состав полей — TmpFile::jsonSerialize
        const file = response.data as { name: string; mimeType: string; data: string };

        Core.utils.file.downloadFromBase64(file.data, file.name, file.mimeType);
      }

      // про отправку сообщаем всегда, когда письмо ушло — скачивался файл или нет
      if (downloadOnly === false) {
        Core.showNotification.success('Уведомление отправлено');
      }
    }

    return response.ok;
  }


  /**
   * Редактирование ЖДД заказа или одной позиции.
   * Логика мутации и сохранения — внутри OrderChangeDatesDialog.
   */
  static editDates(
    target: Order | OrderPosition,
    showCurrencyCode: boolean = false,
    showReasonInput: boolean = false,
  ): Promise<boolean> {
    const isOrder = target instanceof Order;

    return Core.dialogs.OrderPositionSetDeliveryDatesDialog.showAsPromise({
      order:    isOrder ? target : null,
      position: isOrder ? null   : target,
      showCurrencyCode,
      showReasonInput,
    });
  }

  // ============================================================
  // Создание заказов из потребности
  // ============================================================

  /**
   * Создаёт заказы по выбранным вариантам потребности.
   * По итогу сообщает, ушла ли потребность в архив, и предлагает перейти
   * в черновики заказов. true — заказы созданы, реестр потребностей надо обновить.
   */
  static async createOrdersFromDemandV2(demandV2Id: number): Promise<boolean> {
    const demandV2 = await DemandsV2Service.get(demandV2Id);

    if (!demandV2) {
      return false;
    }

    const {
      ordersCount,
      totalQtyUsed,
      totalQtyDesired,
      ignoredQty,
      ignoredOptions,
      orders,
    } = demandV2.previewOrderCreation();

    const ordersWord = Core.utils.string.pluralizeRu(ordersCount, 'заказ', 'заказа', 'заказов');
    const ordersVerb = Core.utils.string.pluralizeRu(ordersCount, 'создан', 'создано', 'создано');

    const lines = [
      `По заявке будет ${ordersVerb} ${ordersCount} ${ordersWord}:`,
      '',
      ...orders.map(order => `• "${order.sellerName}" - ${order.qty} ${demandV2.unit?.name}`),
    ];

    if (ignoredQty.isPositive()) {
      lines.push('');
      lines.push(...ignoredOptions.map(option =>
        `Внимание: из варианта "${option.sellerName}" не войдёт в заказ ${option.qty.format(demandV2.unit)}.`,
      ));
    }

    if (totalQtyUsed.lessThan(totalQtyDesired)) {
      lines.push('');
      lines.push(`Внимание: заявка будет удовлетворена на ${totalQtyUsed.format()} из ${totalQtyDesired.format(demandV2.unit)}.`);
    }

    lines.push('');
    lines.push('Продолжить?');

    if (ignoredQty.isPositive() || totalQtyUsed.lessThan(totalQtyDesired)) {
      const isConfirmed = await Core.dialogs.confirm(
        lines.join('<br>'),
        `${demandV2.name}`,
      );

      if (!isConfirmed) {
        return false;
      }
    }

    const ordersFromDemand = Order.fromDemandV2(demandV2);

    for (const order of ordersFromDemand) {
      await OrdersService.add(order);
    }

    /** Потребность уходит в архив, только если заказы покрывают её целиком */
    const resultLines: string[] = [];

    if (totalQtyUsed.lessThan(totalQtyDesired) === true) {
      resultLines.push(`По выбранному количеству ${ordersVerb} ${ordersCount} ${ordersWord}.`);
      resultLines.push(`Потребность остаётся в работе: заказано ${totalQtyUsed.format()} из ${totalQtyDesired.format(demandV2.unit)}.`);
    }
    else {
      resultLines.push(`${ordersVerb.charAt(0).toUpperCase()}${ordersVerb.slice(1)} ${ordersCount} ${ordersWord}.`);
      resultLines.push('Потребность перенесена в архив.');
    }

    resultLines.push('');
    resultLines.push('Перейти в черновики заказов?');

    const isGoToOrders = await Core.dialogs.confirm(
      resultLines.join('<br>'),
      `${demandV2.name}`,
    );

    if (isGoToOrders === true) {
      await Core.router.push('/flow/orders/draft');
    }

    return true;
  }

  // ============================================================
  // Контекстное меню — заказ
  // ============================================================

  static get contextMenu(): ContextMenuItem[] {
    return [
      new ContextMenuItem(
        'Открыть',
        'open_in_new',
        (order: Order) => { return this.showOrderCard(order); },
        null,
        false,
        true,
      ),
      new ContextMenuItem(
        'Уведомить поставщика',
        'notifications_active',
        (order: Order) => { return this.openNotifyPersonsDialog(order); },
        (order: Order) => order.getReasonCannotNotify(),
      ),
      new ContextMenuItem(
        'Утвердить',
        'done',
        (order: Order) => { return this.confirm(order.id); },
        (order: Order) => order.getReasonCannotApprove(),
      ),
      new ContextMenuItem(
        'Создать копию',
        'content_copy',
        (order: Order) => { return this.showOrderCard(null, null, order); },
        (order: Order) => order.getReasonCannotCopy(),
      ),
      new ContextMenuItem(
        'История действий',
        'history',
        (order: Order) => { this.showFeedDialog(order.id); },
        (order: Order) => order.getReasonCannotAccessFeed(),
      ),
      new ContextMenuItem(
        'Перенос ЖДД',
        'event',
        (order: Order) => { return this.editDates(order); },
        (order: Order) => order.getReasonCannotSetDates(),
      ),
      new ContextMenuItem(
        'Файлы',
        'file_present',
        (order: Order) => { this.openFilesDialog(order); },
        (order: Order) => order.getReasonCannotAccessFiles(),
        false,  // hidden
        true,   // dividerAfter
      ),
      new ContextMenuItem(
        'Удалить',
        'delete',
        (order: Order) => { return this.deleteOrderWithConfirm(order); },
        (order: Order) => order.getReasonCannotDelete(),
      ),
    ];
  }

  // ============================================================
  // Контекстное меню — позиция заказа
  // ============================================================

  static get positionsContextMenu(): ContextMenuItem[] {
    return [
      new ContextMenuItem(
        'Открыть',
        'open_in_new',
        (position: OrderPosition) => { return this.showOrderPositionCard(position); },
      ),
      new ContextMenuItem(
        'Создать платёж',
        'add_shopping_cart',
        (position: OrderPosition) => {
          return Core.dialogs.PaymentRequestCard.showAsPromise({
            orderPositionOrId: position.id
          })
        },
        (position: OrderPosition) => position.getReasonCannotCreatePayment(),
        /** Платежи ведёт покупатель — поставщику пункт не показываем */
        Core.currentPartner.isSeller === true,
      ),
      new ContextMenuItem(
        'Вернуть заказ в черновики',
        'arrow_back',
        (position: OrderPosition) => { return this.unconfirm(position.id); },
        (position: OrderPosition) => position.getReasonCannotUnconfirm(),
        /** Черновиками распоряжается покупатель */
        Core.currentPartner.isSeller === true,
      ),
      new ContextMenuItem(
        'Создать сборку',
        'inventory_2',
        (position: OrderPosition) => { return AssembliesService.showAssemblyCard(position); },
        (position: OrderPosition) => Assembly.getReasonCannotCreateFromOrderPositions(position),
      ),
      new ContextMenuItem(
        'Создать отгрузку',
        'local_shipping',
        (position: OrderPosition) => { return ShipmentsService.showShipmentCard(position); },
        (position: OrderPosition) => Shipment.getReasonCannotCreateFromOrderPositions(position),
      ),
      new ContextMenuItem(
        'Закрыть заказ',
        'cancel',
        (position: OrderPosition) => {
          return Core.dialogs.OrderPositionCloseCard.showAsPromise({
            orderPosition: position
          })
        },
        (position: OrderPosition) => position.getReasonCannotClose(),
      ),
      new ContextMenuItem(
        'Карточка партнера',
        'person',
        (position: OrderPosition) => { PartnersService.showPartnerCard(position.seller.id); },
        null,
        /** Карточка продавца — поставщик и так смотрит на себя */
        Core.currentPartner.isSeller === true,
      ),
      new ContextMenuItem(
        'Уведомить поставщика',
        'notifications_active',
        (position: OrderPosition) => { return this.openNotifyPersonsDialog(position); },
        (position: OrderPosition) => position.getReasonCannotNotify(),
        /** Уведомляет поставщика покупатель — самому себе не пишут */
        Core.currentPartner.isSeller === true,
      ),
      new ContextMenuItem(
        'История заказов',
        'history',
        (position: OrderPosition) => {
          Core.dialogs.OrderPositionsRegistryDialog.show({
            title:         `История покупок ${position.seller.toString()}`,
            params: {
              seller:        position.seller,
              orderCategory: position.orderCategory,
            },
            hiddenColumns: [
              'comment',
              'orderCategory',
              'seller',
              'buyer',
              'documentNumber',
              'desiredReceiptDate',
              'plannedShipmentDate',
              'payment',
              'status',
            ],
          })
        },
      ),
      new ContextMenuItem(
        'История тендера',
        'sell',
        (position: OrderPosition) => { TendersService.showTenderCard(position.tender); },
        (position: OrderPosition) => position.getReasonCannotViewTenderHistory(),
      ),
      new ContextMenuItem(
        'Перенос ЖДД',
        'event',
        // было editPositionDates (несуществующий метод) — editDates принимает Order | OrderPosition
        (position: OrderPosition) => { return this.editDates(position); },
        (position: OrderPosition) => position.getReasonCannotEditDates(),
        false,  // hidden
        true,   // dividerAfter
      ),
      new ContextMenuItem(
        'Удалить',
        'delete',
        (position: OrderPosition) => { return this.deletePositionWithConfirm(position); },
        (position: OrderPosition) => position.getReasonCannotDelete(),
      ),
    ];
  }

  /** Заказы текущего покупателя по номенклатуре для графика истории цен */
  static async getPriceHistory(productId: number): Promise<OrderPricePoint[]> {
    const response = await OrdersDAO.getPriceHistory(productId);

    return Core.utils.hydrateArray(response.data, OrderPricePoint.fromServer);
  }
}

export default OrdersService;
